import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Headphones, 
  Gift, 
  Trophy, 
  Star, 
  Crown, 
  Zap,
  Heart,
  MessageCircle,
  Users,
  TrendingUp,
  Sparkles,
  Shield,
  Clock,
  Award,
  Flame,
  Mic2,
  Play,
  Coins,
  CheckCircle,
  ArrowRight,
  ChevronRight,
  Target,
  Lock,
  Unlock,
  Music,
  Radio,
  PartyPopper,
  Medal,
  Gem,
  Eye,
  Share2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { lock, unlock } from '../utils/scrollLock';

const LISTENER_TIERS = [
  {
    name: "Supporter",
    icon: Heart,
    color: "text-pink-400",
    bgColor: "bg-pink-500/20",
    borderColor: "border-pink-500/30",
    requirements: "Entry-level engagement",
    perks: [
      "Supporter badge on profile",
      "Ability to send virtual gifts",
      "Basic recognition on artist profiles",
      "Access to gifting features"
    ]
  },
  {
    name: "Super Fan",
    icon: Star,
    color: "text-yellow-400",
    bgColor: "bg-yellow-500/20",
    borderColor: "border-yellow-500/30",
    requirements: "Increased gifting + watch time",
    perks: [
      "Super Fan badge",
      "Highlighted chat messages",
      "Reduced slow-mode limits",
      "Priority in chat queue",
      "Exclusive Super Fan emote"
    ]
  },
  {
    name: "VIP Fan",
    icon: Crown,
    color: "text-purple-400",
    bgColor: "bg-purple-500/20",
    borderColor: "border-purple-500/30",
    requirements: "Consistent gifting & engagement",
    perks: [
      "VIP badge with special effects",
      "Custom emojis in chat",
      "Priority message visibility",
      "Top Supporters list placement",
      "Early access to new features",
      "VIP-only live stream access"
    ]
  },
  {
    name: "Legend",
    icon: Trophy,
    color: "text-amber-400",
    bgColor: "bg-gradient-to-br from-amber-500/20 to-orange-500/20",
    borderColor: "border-amber-500/50",
    requirements: "High lifetime support",
    perks: [
      "Legendary badge with animation",
      "Pinned chat messages",
      "Temporary profile flair",
      "Public recognition on artist profiles",
      "Eligibility for artist shoutouts",
      "Hall of Fame placement",
      "Exclusive Legend rewards"
    ]
  }
];

const VIRTUAL_GIFTS = [
  { name: "Mic Drop", icon: Mic2, value: 50, color: "text-blue-400" },
  { name: "Crown", icon: Crown, value: 100, color: "text-yellow-400" },
  { name: "Stage Flame", icon: Flame, value: 200, color: "text-orange-400" },
  { name: "DMV Trophy", icon: Trophy, value: 500, color: "text-amber-400" }
];

const HOW_IT_WORKS_STEPS = [
  {
    step: 1,
    title: "Listen & Watch",
    description: "Tune in to live streams and discover new music from DMV artists",
    icon: Headphones,
    color: "text-green-400"
  },
  {
    step: 2,
    title: "Send Gifts & Tips",
    description: "Show support with virtual gifts during streams and direct tips",
    icon: Gift,
    color: "text-pink-400"
  },
  {
    step: 3,
    title: "Earn Recognition",
    description: "Collect coins, badges, and climb the fan status ranks",
    icon: Award,
    color: "text-yellow-400"
  },
  {
    step: 4,
    title: "Unlock Perks",
    description: "Access exclusive features, visibility, and VIP experiences",
    icon: Sparkles,
    color: "text-purple-400"
  }
];

// Achievement badges that users can unlock
const ACHIEVEMENT_BADGES = [
  { id: 'first_gift', name: 'First Gift', description: 'Sent your first virtual gift', icon: Gift, color: 'text-pink-400', bgColor: 'bg-pink-500/20', xp: 50 },
  { id: 'stream_starter', name: 'Stream Starter', description: 'Watched 5 live streams', icon: Radio, color: 'text-blue-400', bgColor: 'bg-blue-500/20', xp: 75 },
  { id: 'chat_regular', name: 'Chat Regular', description: 'Sent 100 chat messages', icon: MessageCircle, color: 'text-green-400', bgColor: 'bg-green-500/20', xp: 100 },
  { id: 'generous_heart', name: 'Generous Heart', description: 'Gifted 500+ coins worth', icon: Heart, color: 'text-red-400', bgColor: 'bg-red-500/20', xp: 150 },
  { id: 'early_bird', name: 'Early Bird', description: 'Joined a stream in first 5 mins', icon: Clock, color: 'text-amber-400', bgColor: 'bg-amber-500/20', xp: 50 },
  { id: 'music_lover', name: 'Music Lover', description: 'Played 50 tracks', icon: Music, color: 'text-purple-400', bgColor: 'bg-purple-500/20', xp: 75 },
  { id: 'social_butterfly', name: 'Social Butterfly', description: 'Followed 10 artists', icon: Users, color: 'text-cyan-400', bgColor: 'bg-cyan-500/20', xp: 100 },
  { id: 'party_starter', name: 'Party Starter', description: 'Shared content 25 times', icon: Share2, color: 'text-orange-400', bgColor: 'bg-orange-500/20', xp: 125 },
  { id: 'crown_dropper', name: 'Crown Dropper', description: 'Sent 10 Crown gifts', icon: Crown, color: 'text-yellow-400', bgColor: 'bg-yellow-500/20', xp: 200 },
  { id: 'legend_maker', name: 'Legend Maker', description: 'Reach Legend tier', icon: Trophy, color: 'text-amber-400', bgColor: 'bg-gradient-to-br from-amber-500/20 to-orange-500/20', xp: 500 },
  { id: 'flame_thrower', name: 'Flame Thrower', description: 'Sent 50 Stage Flame gifts', icon: Flame, color: 'text-orange-400', bgColor: 'bg-orange-500/20', xp: 300 },
  { id: 'diamond_hands', name: 'Diamond Hands', description: 'Spent 10,000+ coins total', icon: Gem, color: 'text-cyan-300', bgColor: 'bg-cyan-500/20', xp: 1000 },
];

// XP actions and their point values
const XP_ACTIONS = [
  { action: 'Watch 1 min of stream', xp: 1, icon: Eye },
  { action: 'Send a chat message', xp: 2, icon: MessageCircle },
  { action: 'Send a virtual gift', xp: 10, icon: Gift },
  { action: 'Tip an artist', xp: 25, icon: Coins },
  { action: 'Share content', xp: 5, icon: Share2 },
  { action: 'Follow an artist', xp: 15, icon: Heart },
  { action: 'Play a track', xp: 1, icon: Play },
];

// Tier XP thresholds
const TIER_THRESHOLDS = [
  { tier: 'Supporter', minXP: 0, maxXP: 500, color: 'text-pink-400', bgColor: 'bg-pink-500' },
  { tier: 'Super Fan', minXP: 500, maxXP: 2000, color: 'text-yellow-400', bgColor: 'bg-yellow-500' },
  { tier: 'VIP Fan', minXP: 2000, maxXP: 5000, color: 'text-purple-400', bgColor: 'bg-purple-500' },
  { tier: 'Legend', minXP: 5000, maxXP: Infinity, color: 'text-amber-400', bgColor: 'bg-gradient-to-r from-amber-500 to-orange-500' },
];

export default function ListenerPerksPage() {
  // Demo progress state - in real app this would come from user data
  const [demoXP, setDemoXP] = useState(1250);
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [showUnlockAnimation, setShowUnlockAnimation] = useState(false);
  
  // Manage scroll lock for badge modal
  useEffect(() => {
    if (selectedBadge) {
      lock();
    }
    return () => {
      if (selectedBadge) {
        unlock();
      }
    };
  }, [selectedBadge]);
  
  // Calculate current tier based on XP
  const getCurrentTier = (xp) => {
    for (let i = TIER_THRESHOLDS.length - 1; i >= 0; i--) {
      if (xp >= TIER_THRESHOLDS[i].minXP) {
        return { ...TIER_THRESHOLDS[i], index: i };
      }
    }
    return { ...TIER_THRESHOLDS[0], index: 0 };
  };
  
  const currentTier = getCurrentTier(demoXP);
  const nextTier = TIER_THRESHOLDS[currentTier.index + 1];
  const progressToNext = nextTier 
    ? ((demoXP - currentTier.minXP) / (nextTier.minXP - currentTier.minXP)) * 100 
    : 100;
  
  // Simulate XP gain for demo
  const simulateXPGain = (amount) => {
    setDemoXP(prev => {
      const newXP = prev + amount;
      const oldTier = getCurrentTier(prev);
      const newTierData = getCurrentTier(newXP);
      if (newTierData.index > oldTier.index) {
        setShowUnlockAnimation(true);
        setTimeout(() => setShowUnlockAnimation(false), 3000);
      }
      return newXP;
    });
  };
  
  return (
    <div className="min-h-screen" data-testid="listener-perks-page">
      {/* Hero Section */}
      <section className="relative py-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/20 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-purple-500/10 via-transparent to-transparent" />
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <Headphones className="w-10 h-10 text-primary" />
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold">
                Listener Perks Program
              </h1>
            </div>
            
            <p className="text-xl sm:text-2xl text-muted-foreground mb-6">
              Support the artists you love. Get recognized. Unlock exclusive perks.
            </p>
            
            <p className="text-base text-muted-foreground max-w-2xl mx-auto mb-8">
              The Listener Perks Program rewards fans who actively support artists through listening, 
              gifting, tipping, and engagement. The more you support, the more perks and recognition you unlock.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/live">
                <Button size="lg" className="gap-2">
                  <Play className="w-5 h-5" />
                  Watch Live Streams
                </Button>
              </Link>
              <Link to="/coins">
                <Button size="lg" variant="outline" className="gap-2">
                  <Coins className="w-5 h-5" />
                  Get Coins
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-4 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">How It Works</Badge>
            <h2 className="text-3xl font-bold mb-4">Earn Perks in 4 Simple Steps</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Listening alone earns progress, but gifting and engagement accelerate your perks.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {HOW_IT_WORKS_STEPS.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="p-6 h-full relative overflow-hidden group hover:border-primary/50 transition-colors">
                  <div className="absolute top-4 right-4 text-6xl font-bold text-muted-foreground/20 group-hover:text-primary/10 transition-colors">
                    {step.step}
                  </div>
                  <div className={`w-12 h-12 rounded-xl ${step.color.replace('text-', 'bg-').replace('-400', '-500/20')} flex items-center justify-center mb-4`}>
                    <step.icon className={`w-6 h-6 ${step.color}`} />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">{step.title}</h3>
                  <p className="text-sm text-muted-foreground">{step.description}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tier Unlock Animation Overlay */}
      <AnimatePresence>
        {showUnlockAnimation && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm"
            onClick={() => setShowUnlockAnimation(false)}
          >
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              exit={{ scale: 0, rotate: 180 }}
              transition={{ type: "spring", damping: 15 }}
              className="text-center"
            >
              <motion.div
                animate={{ 
                  boxShadow: [
                    "0 0 20px rgba(234, 179, 8, 0.3)",
                    "0 0 60px rgba(234, 179, 8, 0.6)",
                    "0 0 20px rgba(234, 179, 8, 0.3)"
                  ]
                }}
                transition={{ repeat: Infinity, duration: 1.5 }}
                className="w-32 h-32 rounded-full bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center mx-auto mb-6"
              >
                <PartyPopper className="w-16 h-16 text-white" />
              </motion.div>
              <motion.h2 
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-4xl font-bold mb-2 bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent"
              >
                Tier Unlocked!
              </motion.h2>
              <motion.p
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="text-xl text-muted-foreground"
              >
                You've reached <span className={currentTier.color}>{currentTier.tier}</span> status!
              </motion.p>
              <motion.p
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.7 }}
                className="text-sm text-muted-foreground mt-4"
              >
                Click anywhere to continue
              </motion.p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Interactive Progress Tracker */}
      <section className="py-16 px-4" data-testid="progress-tracker-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4 gap-2">
              <Target className="w-3 h-3" /> Your Progress
            </Badge>
            <h2 className="text-3xl font-bold mb-4">Track Your Journey</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Every action earns XP. Watch your progress and unlock higher tiers!
            </p>
          </div>
          
          {/* Progress Card */}
          <Card className="p-8 mb-8 bg-gradient-to-br from-primary/5 to-purple-500/5 border-primary/20">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              {/* Current Status */}
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <motion.div 
                    className={`w-20 h-20 rounded-2xl ${currentTier.bgColor}/20 flex items-center justify-center`}
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ repeat: Infinity, duration: 2 }}
                  >
                    {currentTier.tier === 'Supporter' && <Heart className={`w-10 h-10 ${currentTier.color}`} />}
                    {currentTier.tier === 'Super Fan' && <Star className={`w-10 h-10 ${currentTier.color}`} />}
                    {currentTier.tier === 'VIP Fan' && <Crown className={`w-10 h-10 ${currentTier.color}`} />}
                    {currentTier.tier === 'Legend' && <Trophy className={`w-10 h-10 ${currentTier.color}`} />}
                  </motion.div>
                  <div>
                    <p className="text-sm text-muted-foreground">Current Tier</p>
                    <h3 className={`text-2xl font-bold ${currentTier.color}`}>{currentTier.tier}</h3>
                    <p className="text-sm text-muted-foreground">{demoXP.toLocaleString()} XP earned</p>
                  </div>
                </div>
                
                {/* Progress to next tier */}
                {nextTier && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Progress to {nextTier.tier}</span>
                      <span className={nextTier.color}>{Math.round(progressToNext)}%</span>
                    </div>
                    <div className="relative">
                      <Progress value={progressToNext} className="h-3" />
                      <motion.div 
                        className="absolute top-0 left-0 h-3 rounded-full bg-gradient-to-r from-transparent via-white/30 to-transparent"
                        animate={{ x: ['-100%', '200%'] }}
                        transition={{ repeat: Infinity, duration: 2, ease: "linear" }}
                        style={{ width: '50%' }}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground text-center">
                      {(nextTier.minXP - demoXP).toLocaleString()} XP to unlock {nextTier.tier}
                    </p>
                  </div>
                )}
                
                {!nextTier && (
                  <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-xl p-4 text-center">
                    <Trophy className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                    <p className="text-amber-400 font-semibold">Maximum Tier Achieved!</p>
                    <p className="text-sm text-muted-foreground">You're a Legend!</p>
                  </div>
                )}
              </div>
              
              {/* Tier Progression Visual */}
              <div className="hidden lg:block">
                <div className="relative">
                  {/* Tier Steps */}
                  <div className="flex justify-between items-end">
                    {TIER_THRESHOLDS.map((tier, index) => {
                      const isUnlocked = demoXP >= tier.minXP;
                      const isCurrent = currentTier.tier === tier.tier;
                      return (
                        <motion.div
                          key={tier.tier}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="flex flex-col items-center"
                        >
                          <motion.div
                            className={`w-14 h-14 rounded-xl flex items-center justify-center mb-2 transition-all ${
                              isUnlocked 
                                ? `${tier.bgColor}/30 border-2 border-current ${tier.color}` 
                                : 'bg-muted/50 border-2 border-muted text-muted-foreground'
                            } ${isCurrent ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : ''}`}
                            animate={isCurrent ? { scale: [1, 1.1, 1] } : {}}
                            transition={{ repeat: Infinity, duration: 2 }}
                          >
                            {isUnlocked ? <Unlock className="w-6 h-6" /> : <Lock className="w-6 h-6" />}
                          </motion.div>
                          <span className={`text-xs font-medium ${isUnlocked ? tier.color : 'text-muted-foreground'}`}>
                            {tier.tier}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {tier.minXP.toLocaleString()} XP
                          </span>
                        </motion.div>
                      );
                    })}
                  </div>
                  
                  {/* Connection Line */}
                  <div className="absolute bottom-16 left-7 right-7 h-1 bg-muted rounded-full -z-10">
                    <motion.div 
                      className="h-full bg-gradient-to-r from-pink-500 via-yellow-500 to-amber-500 rounded-full"
                      initial={{ width: '0%' }}
                      animate={{ width: `${(currentTier.index / (TIER_THRESHOLDS.length - 1)) * 100}%` }}
                      transition={{ duration: 1, ease: "easeOut" }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </Card>
          
          {/* XP Actions */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {XP_ACTIONS.slice(0, 4).map((action, index) => (
              <motion.div
                key={action.action}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Card 
                  className="p-4 cursor-pointer hover:border-primary/50 transition-all group"
                  onClick={() => simulateXPGain(action.xp)}
                  data-testid={`xp-action-${index}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                      <action.icon className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">{action.action}</p>
                      <p className="text-xs text-green-400">+{action.xp} XP</p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
          
          <p className="text-center text-xs text-muted-foreground mt-4">
            Click cards above to simulate earning XP!
          </p>
        </div>
      </section>

      {/* Achievement Badges Gallery */}
      <section className="py-16 px-4 bg-muted/30" data-testid="badges-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4 gap-2">
              <Medal className="w-3 h-3" /> Achievements
            </Badge>
            <h2 className="text-3xl font-bold mb-4">Collect Achievement Badges</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Complete challenges to unlock special badges. Each badge earns bonus XP!
            </p>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {ACHIEVEMENT_BADGES.map((badge, index) => {
              // Simulate some badges being unlocked
              const isUnlocked = index < 4;
              return (
                <motion.div
                  key={badge.id}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="cursor-pointer"
                  onClick={() => setSelectedBadge(badge)}
                >
                  <Card className={`p-4 text-center h-full transition-all ${
                    isUnlocked 
                      ? 'border-primary/30 hover:border-primary/60' 
                      : 'opacity-60 grayscale hover:opacity-80'
                  }`}>
                    <motion.div 
                      className={`w-14 h-14 rounded-2xl ${badge.bgColor} flex items-center justify-center mx-auto mb-3 ${
                        isUnlocked ? '' : 'bg-muted'
                      }`}
                      animate={isUnlocked ? { 
                        boxShadow: [
                          "0 0 0px rgba(255,255,255,0)",
                          "0 0 20px rgba(255,255,255,0.1)",
                          "0 0 0px rgba(255,255,255,0)"
                        ]
                      } : {}}
                      transition={{ repeat: Infinity, duration: 2 }}
                    >
                      <badge.icon className={`w-7 h-7 ${isUnlocked ? badge.color : 'text-muted-foreground'}`} />
                    </motion.div>
                    <h4 className="text-xs font-medium mb-1 truncate">{badge.name}</h4>
                    <div className="flex items-center justify-center gap-1">
                      {isUnlocked ? (
                        <CheckCircle className="w-3 h-3 text-green-400" />
                      ) : (
                        <Lock className="w-3 h-3 text-muted-foreground" />
                      )}
                      <span className={`text-xs ${isUnlocked ? 'text-green-400' : 'text-muted-foreground'}`}>
                        +{badge.xp} XP
                      </span>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
          
          {/* Badge Detail Modal */}
          <AnimatePresence>
            {selectedBadge && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-center justify-center bg-background/60 backdrop-blur-sm p-4"
                onClick={() => setSelectedBadge(null)}
              >
                <motion.div
                  initial={{ scale: 0.9, y: 20 }}
                  animate={{ scale: 1, y: 0 }}
                  exit={{ scale: 0.9, y: 20 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Card className="p-8 max-w-sm text-center">
                    <motion.div 
                      className={`w-24 h-24 rounded-3xl ${selectedBadge.bgColor} flex items-center justify-center mx-auto mb-4`}
                      animate={{ rotate: [0, 5, -5, 0] }}
                      transition={{ repeat: Infinity, duration: 2 }}
                    >
                      <selectedBadge.icon className={`w-12 h-12 ${selectedBadge.color}`} />
                    </motion.div>
                    <h3 className="text-xl font-bold mb-2">{selectedBadge.name}</h3>
                    <p className="text-muted-foreground mb-4">{selectedBadge.description}</p>
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                      +{selectedBadge.xp} XP Reward
                    </Badge>
                    <Button 
                      variant="ghost" 
                      className="mt-4 w-full"
                      onClick={() => setSelectedBadge(null)}
                    >
                      Close
                    </Button>
                  </Card>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Listener Tiers */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">Fan Tiers</Badge>
            <h2 className="text-3xl font-bold mb-4">Climb the Ranks</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Progress through tiers to unlock increasingly powerful perks and recognition.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {LISTENER_TIERS.map((tier, index) => (
              <motion.div
                key={tier.name}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className={`p-6 h-full ${tier.borderColor} border-2 hover:shadow-lg transition-shadow`}>
                  <div className="flex items-start gap-4 mb-4">
                    <div className={`w-14 h-14 rounded-xl ${tier.bgColor} flex items-center justify-center flex-shrink-0`}>
                      <tier.icon className={`w-7 h-7 ${tier.color}`} />
                    </div>
                    <div>
                      <h3 className={`font-bold text-xl ${tier.color}`}>{tier.name}</h3>
                      <p className="text-sm text-muted-foreground">{tier.requirements}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    {tier.perks.map((perk, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <CheckCircle className={`w-4 h-4 ${tier.color} flex-shrink-0`} />
                        <span>{perk}</span>
                      </div>
                    ))}
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Top Fan System */}
      <section className="py-16 px-4 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge variant="outline" className="mb-4">Top Fans</Badge>
              <h2 className="text-3xl font-bold mb-4">Get Recognized as a Top Supporter</h2>
              <p className="text-muted-foreground mb-6">
                Each artist has their own Top Fans list. The more you support an artist, 
                the higher you climb on their leaderboard.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0">
                    <Gift className="w-4 h-4 text-green-400" />
                  </div>
                  <div>
                    <h4 className="font-medium">Gifts Sent</h4>
                    <p className="text-sm text-muted-foreground">Virtual gifts contribute to your ranking</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                    <Coins className="w-4 h-4 text-blue-400" />
                  </div>
                  <div>
                    <h4 className="font-medium">Tips Given</h4>
                    <p className="text-sm text-muted-foreground">Direct tips heavily boost your status</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-4 h-4 text-purple-400" />
                  </div>
                  <div>
                    <h4 className="font-medium">Watch Time</h4>
                    <p className="text-sm text-muted-foreground">Time spent in streams counts</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-4 h-4 text-orange-400" />
                  </div>
                  <div>
                    <h4 className="font-medium">Chat Participation</h4>
                    <p className="text-sm text-muted-foreground">Active engagement in chat adds up</p>
                  </div>
                </div>
              </div>
            </div>
            
            <Card className="p-6 bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-500/30">
              <div className="flex items-center gap-2 mb-4">
                <Trophy className="w-5 h-5 text-amber-400" />
                <h3 className="font-semibold">Top Fan Rules</h3>
              </div>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                  <span>Rankings calculated on a <strong>rolling 30-day window</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                  <span>Top Fans appear prominently on artist profiles</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                  <span>Badges displayed in chat and live streams</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                  <span>Top 3 fans get special recognition</span>
                </li>
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* Virtual Gifts */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">Virtual Gifts</Badge>
            <h2 className="text-3xl font-bold mb-4">Send Love During Live Streams</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Virtual gifts are fun, animated items you can send during live streams to show support. 
              Each gift triggers a visual effect, increases your fan status, and supports the artist directly.
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {VIRTUAL_GIFTS.map((gift, index) => (
              <motion.div
                key={gift.name}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="p-6 text-center hover:border-primary/50 transition-colors cursor-pointer group">
                  <div className={`w-16 h-16 rounded-2xl ${gift.color.replace('text-', 'bg-').replace('-400', '-500/20')} flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform`}>
                    <gift.icon className={`w-8 h-8 ${gift.color}`} />
                  </div>
                  <h4 className="font-medium mb-1">{gift.name}</h4>
                  <div className="flex items-center justify-center gap-1 text-sm text-muted-foreground">
                    <Coins className="w-3 h-3" />
                    <span>{gift.value} coins</span>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
          
          <div className="text-center">
            <Link to="/coins">
              <Button variant="outline" className="gap-2">
                <Coins className="w-4 h-4" />
                Get Coins to Send Gifts
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Non-Cash Rewards */}
      <section className="py-16 px-4 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">Rewards</Badge>
            <h2 className="text-3xl font-bold mb-4">Unlock Special Rewards</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Beyond tier perks, earn achievement badges and exclusive rewards for your dedication.
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <Award className="w-5 h-5 text-green-400" />
                </div>
                <h4 className="font-medium">Achievement Badges</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                First Gift, 100 Gifts, Stream Regular, Early Supporter, and more
              </p>
            </Card>
            
            <Card className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-purple-400" />
                </div>
                <h4 className="font-medium">Profile Decorations</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Special borders, frames, and flair to customize your profile
              </p>
            </Card>
            
            <Card className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-blue-400" />
                </div>
                <h4 className="font-medium">Early Access</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Be first to try new platform features before everyone else
              </p>
            </Card>
            
            <Card className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-pink-500/20 flex items-center justify-center">
                  <Users className="w-5 h-5 text-pink-400" />
                </div>
                <h4 className="font-medium">Exclusive Streams</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Access fan-only live streams when artists enable them
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Transparency */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <Card className="p-8 border-border">
            <div className="flex items-center gap-3 mb-6">
              <Shield className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold">Transparency & Fair Use</h2>
            </div>
            
            <div className="grid sm:grid-cols-2 gap-6 text-sm text-muted-foreground">
              <div className="space-y-3">
                <p className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                  <span>Gifts and coins do not guarantee personal interaction with artists</span>
                </p>
                <p className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                  <span>Perks are non-transferable between accounts</span>
                </p>
              </div>
              <div className="space-y-3">
                <p className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                  <span>No refunds on virtual goods unless required by law</span>
                </p>
                <p className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                  <span>All perks subject to community guidelines and moderation rules</span>
                </p>
              </div>
            </div>
            
            <div className="mt-6 pt-6 border-t border-border">
              <Link to="/perks-terms" className="text-sm text-primary hover:underline">
                Read full Perks Terms & Conditions →
              </Link>
            </div>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-primary/10 to-transparent">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Start Supporting?</h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            Join thousands of fans who are already earning perks and getting recognized 
            for supporting their favorite DMV artists.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/discover">
              <Button size="lg" className="gap-2">
                <Heart className="w-5 h-5" />
                Start Supporting Artists
              </Button>
            </Link>
            <Link to="/live">
              <Button size="lg" variant="outline" className="gap-2">
                <Play className="w-5 h-5" />
                View Live Streams
              </Button>
            </Link>
            <Link to="/coins">
              <Button size="lg" variant="secondary" className="gap-2">
                <Coins className="w-5 h-5" />
                Buy Coins
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
